/**
 * Bulk-generate voucher codes -- phase 6b, Task 3.
 * POST /admin/discounts/promotions/:id/vouchers/bulk (MANUAL only, one
 * transaction, up to 500).
 *
 * Payload is always the full `{ count, prefix, length, expiresAt }` with a
 * blank expiry as `null` (never `""`). On success the same dialog switches
 * to the result view: the generated codes with "Copy all" (primary -- the
 * artifact/viewer sandbox may block downloads) and a "Download CSV" data-URI
 * link (secondary). The list invalidates `['discounts']` so the counts on
 * the header, the promotions list and the stats bar refresh.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { ContentCopy as CopyIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import type { BulkGenerateBody, BulkGenerateResponse } from '../types';
import { localInputToIso } from './PromotionDialog';

/** schemas/vouchers.ts bulkGenerateBody.prefix */
export const PREFIX_RE = /^[A-Z0-9-]{0,10}$/;
export const COUNT_MIN = 1;
export const COUNT_MAX = 500;
export const LENGTH_MIN = 6;
export const LENGTH_MAX = 12;

/** Pure: validate the form and build the exact request body (or field errors). */
export const buildBulkBody = (f: {
  count: string;
  prefix: string;
  length: string;
  expires: string;
}): { body: BulkGenerateBody } | { errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  const count = Number(f.count.trim());
  if (!/^\d+$/.test(f.count.trim()) || count < COUNT_MIN || count > COUNT_MAX) {
    errors.count = `Between ${COUNT_MIN} and ${COUNT_MAX}.`;
  }
  const prefix = f.prefix.trim();
  if (!PREFIX_RE.test(prefix)) errors.prefix = 'Up to 10 characters: A–Z, 0–9 or dash.';
  const length = Number(f.length.trim());
  if (!/^\d+$/.test(f.length.trim()) || length < LENGTH_MIN || length > LENGTH_MAX) {
    errors.length = `Between ${LENGTH_MIN} and ${LENGTH_MAX} characters.`;
  }
  if (Object.keys(errors).length) return { errors };
  return { body: { count, prefix, length, expiresAt: localInputToIso(f.expires) } };
};

export const toCsv = (codes: string[]): string => `code\n${codes.join('\n')}\n`;

export interface BulkCodesDialogProps {
  open: boolean;
  onClose: () => void;
  promotionId: string;
  promotionName?: string;
}

export const BulkCodesDialog: React.FC<BulkCodesDialogProps> = ({ open, onClose, promotionId, promotionName }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const [count, setCount] = useState('50');
  const [prefix, setPrefix] = useState('');
  const [length, setLength] = useState('8');
  const [expires, setExpires] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkGenerateResponse | null>(null);

  useEffect(() => {
    if (open) {
      setCount('50');
      setPrefix('');
      setLength('8');
      setExpires('');
      setErrors({});
      setTopError(null);
      setResult(null);
    }
  }, [open]);

  const generate = useMutation({
    mutationFn: (body: BulkGenerateBody) => discountsApi.bulkGenerate(promotionId, body),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: qk.all });
      setResult(res);
      notify(`${res.created} code${res.created === 1 ? '' : 's'} generated`, { type: 'success' });
    },
    onError: (err) => {
      const f = toApiFailure(err);
      // errors.ts never keys a whole-body 422 under '' -- that case arrives with
      // empty fieldErrors and lands in `message` below, like every other status.
      if (f.status === 422 && Object.keys(f.fieldErrors).length) {
        setErrors(f.fieldErrors);
        setTopError(null);
        return;
      }
      setErrors({});
      setTopError(f.message);
    },
  });

  const submit = () => {
    const built = buildBulkBody({ count, prefix, length, expires });
    if ('errors' in built) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    setTopError(null);
    generate.mutate(built.body);
  };

  const csvHref = useMemo(() => (result ? `data:text/csv;charset=utf-8,${encodeURIComponent(toCsv(result.codes))}` : ''), [result]);
  const csvName = `${(promotionName ?? 'codes').replace(/[^A-Za-z0-9-]+/g, '-').toLowerCase()}-codes.csv`;

  const copyAll = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.codes.join('\n'));
      notify(`Copied ${result.codes.length} code${result.codes.length === 1 ? '' : 's'}`, { type: 'info' });
    } catch {
      notify('Copy failed — select the codes and copy them by hand.', { type: 'warning' });
    }
  };

  const busy = generate.isPending;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{result ? `${result.created} codes generated` : 'Generate codes'}</DialogTitle>
      <DialogContent>
        {result ? (
          <>
            <DialogContentText sx={{ mb: 1.5 }}>
              These codes are now live under {promotionName ?? 'this promotion'}. Copy them now — they are also listed on the Codes tab.
            </DialogContentText>
            <Box
              component="pre"
              aria-label="Generated codes"
              sx={{
                m: 0,
                p: 1.5,
                maxHeight: 280,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: 13,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {result.codes.map((c) => (
                <Box component="span" key={c} sx={{ display: 'block' }}>
                  {c}
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <>
            {topError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {topError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                id="bulk-count"
                label="How many"
                type="number"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                required
                autoFocus
                disabled={busy}
                error={Boolean(errors.count)}
                helperText={errors.count ?? `${COUNT_MIN}–${COUNT_MAX}, created in one go.`}
                slotProps={{ htmlInput: { min: COUNT_MIN, max: COUNT_MAX, step: 1 } }}
              />
              <TextField
                id="bulk-prefix"
                label="Prefix"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                disabled={busy}
                error={Boolean(errors.prefix)}
                helperText={errors.prefix ?? 'Optional, up to 10 characters (A–Z, 0–9, dash), e.g. WIN-.'}
                slotProps={{ htmlInput: { maxLength: 10, style: { fontFamily: 'monospace' } } }}
              />
              <TextField
                id="bulk-length"
                label="Random part length"
                type="number"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                disabled={busy}
                error={Boolean(errors.length)}
                helperText={errors.length ?? `${LENGTH_MIN}–${LENGTH_MAX} characters after the prefix.`}
                slotProps={{ htmlInput: { min: LENGTH_MIN, max: LENGTH_MAX, step: 1 } }}
              />
              <TextField
                id="bulk-expiresAt"
                label="Expires"
                type="datetime-local"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                disabled={busy}
                error={Boolean(errors.expiresAt)}
                helperText={errors.expiresAt ?? 'Leave blank for no expiry.'}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button component="a" href={csvHref} download={csvName} startIcon={<DownloadIcon />} variant="outlined">
              Download CSV
            </Button>
            <Button variant="contained" startIcon={<CopyIcon />} onClick={copyAll}>
              Copy all
            </Button>
            <Button onClick={onClose}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="contained" onClick={submit} disabled={busy}>
              Generate
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkCodesDialog;
